import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';

import { MemberFormData } from '../types/member.types';
import { useRoles } from '../hooks/use-role';

import styles from './basic-information.module.css';

import { convertToHalfWidthKatakana } from '@/features/companies/utils/validations';
import { getStylesForEditableField } from '@/utils/helpers';
import { PaperBoxStyles } from '@/components/styles';

export enum AuthorityChangeType {
  ADD = 'add',
  REMOVE = 'remove',
  TOGGLE = 'toggle',
}

interface BasicInformationSectionProps {
  formData: MemberFormData;
  memberData: MemberFormData | null;
  onUpdateField: (field: keyof MemberFormData, value: MemberFormData[keyof MemberFormData]) => void;
  isFieldValid: (field: keyof MemberFormData) => boolean;
  getFieldError: (field: keyof MemberFormData) => string | undefined;
  isEditMode?: boolean;
}

// Exported pure helper to enable exhaustive unit tests without altering behavior
export const handleChangeAuthorityPure = (
  action: AuthorityChangeType,
  roleId: string,
  formData: MemberFormData,
  onUpdateField: (field: keyof MemberFormData, value: MemberFormData[keyof MemberFormData]) => void
) => {
  switch (action) {
    case AuthorityChangeType.ADD:
      if (!formData.authorities.includes(roleId)) {
        onUpdateField('authorities', [...formData.authorities, roleId]);
      }
      break;
    case AuthorityChangeType.REMOVE:
      onUpdateField(
        'authorities',
        formData.authorities.filter((id) => id !== roleId)
      );
      break;
    case AuthorityChangeType.TOGGLE:
      onUpdateField(
        'authorities',
        formData.authorities.includes(roleId)
          ? formData.authorities.filter((id) => id !== roleId)
          : [...formData.authorities, roleId]
      );
      break;
  }
};

export const BasicInformationSection = ({
  formData,
  memberData,
  onUpdateField,
  isFieldValid,
  getFieldError,
  isEditMode = false,
}: BasicInformationSectionProps) => {
  const handleDepartmentChange = (value: string) => {
    onUpdateField('isSelectable', value);
  };
  const { roles } = useRoles();

  const personInChargeStatus = [
    {
      id: '1',
      name: '表示',
    },
    {
      id: '0',
      name: '非表示',
    },
  ];

  const handleChangeAuthority = (action: AuthorityChangeType, roleId: string) => {
    handleChangeAuthorityPure(action, roleId, formData, onUpdateField);
  };

  const handleChangeKanaInput = (field: keyof MemberFormData, value: string) => {
    const convertedValue = convertToHalfWidthKatakana(value);
    onUpdateField(field, convertedValue);
  };

  return (
    <PaperBoxStyles elevation={1} sx={{ mb: 3, paddingBottom: '32px' }}>
      <Typography variant='h5' fontWeight='bold' className={styles.sectionTitle}>
        担当者基本情報
      </Typography>

      <Box className={styles.sectionGrid}>
        <Box className={styles.row}>
          <TextField
            required
            fullWidth
            label='担当者ID'
            placeholder='担当者IDを入力'
            value={formData.code || ''}
            onChange={(e) => onUpdateField('code', e.target.value)}
            error={!isFieldValid('code')}
            helperText={getFieldError('code')}
            slotProps={{ input: { inputProps: { maxLength: 8 } } }}
            disabled={isEditMode}
            sx={
              isEditMode
                ? {
                    '& .MuiInputBase-root': {
                      backgroundColor: '#f5f5f5',
                      color: '#666666',
                    },
                    '& .MuiInputLabel-root': {
                      color: '#666666',
                    },
                  }
                : {}
            }
          />
        </Box>
        <Box className={styles.row}>
          <TextField
            required
            fullWidth
            label='姓'
            placeholder='姓を入力'
            value={formData.lastName || ''}
            onChange={(e) => onUpdateField('lastName', e.target.value)}
            error={!isFieldValid('lastName')}
            helperText={getFieldError('lastName')}
            slotProps={{
              input: {
                inputProps: { maxLength: 64 },
                sx: getStylesForEditableField(
                  isEditMode && !!memberData,
                  memberData?.lastName,
                  formData.lastName
                ),
              },
            }}
          />
          <TextField
            required
            fullWidth
            label='名'
            placeholder='名を入力'
            value={formData.firstName || ''}
            onChange={(e) => onUpdateField('firstName', e.target.value)}
            error={!isFieldValid('firstName')}
            helperText={getFieldError('firstName')}
            slotProps={{
              input: {
                inputProps: { maxLength: 64 },
                sx: getStylesForEditableField(
                  isEditMode && !!memberData,
                  memberData?.firstName,
                  formData.firstName
                ),
              },
            }}
          />
        </Box>
        <Box className={styles.row}>
          <TextField
            fullWidth
            label='姓の半角ｶﾅ'
            placeholder='姓の半角ｶﾅを入力'
            value={formData.lastNameYomi || ''}
            onChange={(e) => handleChangeKanaInput('lastNameYomi', e.target.value)}
            error={!isFieldValid('lastNameYomi')}
            helperText={getFieldError('lastNameYomi')}
            slotProps={{
              input: {
                inputProps: { maxLength: 64 },
                sx: getStylesForEditableField(
                  isEditMode && !!memberData,
                  memberData?.lastNameYomi,
                  formData.lastNameYomi
                ),
              },
            }}
          />
          <TextField
            fullWidth
            label='名の半角ｶﾅ'
            placeholder='名の半角ｶﾅを入力'
            value={formData.firstNameYomi || ''}
            onChange={(e) => handleChangeKanaInput('firstNameYomi', e.target.value)}
            error={!isFieldValid('firstNameYomi')}
            helperText={getFieldError('firstNameYomi')}
            slotProps={{
              input: {
                inputProps: { maxLength: 64 },
                sx: getStylesForEditableField(
                  isEditMode && !!memberData,
                  memberData?.firstNameYomi,
                  formData.firstNameYomi
                ),
              },
            }}
          />
        </Box>

        <Box className={styles.row}>
          <TextField
            fullWidth
            label='電話番号'
            placeholder='電話番号を入力'
            value={formData.tel || ''}
            onChange={(e) => onUpdateField('tel', e.target.value)}
            error={!isFieldValid('tel')}
            helperText={getFieldError('tel')}
            slotProps={{
              input: {
                inputProps: { maxLength: 13 },
                sx: getStylesForEditableField(
                  isEditMode && !!memberData,
                  memberData?.tel,
                  formData.tel
                ),
              },
            }}
          />
        </Box>

        <Box className={styles.row}>
          <FormControl fullWidth required error={!isFieldValid('isSelectable')}>
            <InputLabel>担当者表示</InputLabel>
            <Select
              value={formData.isSelectable?.toString() || ''}
              onChange={(e) => handleDepartmentChange(e.target.value)}
              label='担当者表示'
              sx={getStylesForEditableField(
                isEditMode && !!memberData,
                memberData?.isSelectable,
                formData.isSelectable
              )}
            >
              {personInChargeStatus?.map((status) => (
                <MenuItem key={status.id} value={status.id}>
                  {status.name}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{getFieldError('isSelectable')}</FormHelperText>
          </FormControl>
        </Box>
      </Box>

      <Typography className={styles.subTitle}>
        権限選択<span className={styles.required}>*</span>
      </Typography>
      <Box
        className={styles.authoritySection}
        sx={getStylesForEditableField(
          isEditMode && !!memberData,
          memberData?.authorities,
          formData.authorities
        )}
      >
        <Typography className={styles.authorityTitle}>複数選択が可能です。</Typography>
        <FormGroup className={styles.authorityGroup} aria-required>
          {roles.map((role) => (
            <FormControlLabel
              key={role.id}
              className={styles.authorityLabel}
              onChange={(_, checked) =>
                handleChangeAuthority(
                  checked ? AuthorityChangeType.ADD : AuthorityChangeType.REMOVE,
                  role.id
                )
              }
              control={<Checkbox checked={formData.authorities.includes(role.id)} />}
              label={role.name}
              value={role.id}
            />
          ))}
        </FormGroup>
        <FormHelperText className={styles.errorHelperText}>
          {getFieldError('authorities')}
        </FormHelperText>
      </Box>
    </PaperBoxStyles>
  );
};
